import { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ApiError } from '../../services/api';

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;

const OutageContainer = styled.div<{ $variant: 'banner' | 'card' | 'inline' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme, $variant }) => 
    $variant === 'banner' ? theme.spacing.xl : 
    $variant === 'card' ? theme.spacing['2xl'] : theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.background};
  border: 2px solid ${({ theme }) => theme.colors.error}40;
  border-radius: ${({ theme, $variant }) => 
    $variant === 'banner' ? theme.borderRadius.md : theme.borderRadius.lg};
  text-align: center;
  
  ${({ $variant }) => $variant === 'banner' && `
    border-left: none;
    border-right: none;
    border-radius: 0;
  `}
`;

const StatusIcon = styled.div<{ $status: 'outage' | 'degraded' | 'checking' }>`
  font-size: ${({ $status }) => $status === 'outage' ? '64px' : '48px'};
  animation: ${({ $status }) => $status === 'checking' ? pulse : 'none'} 2s infinite;
  
  ${({ $status }) => {
    switch ($status) {
      case 'outage':
        return '⚠️';
      case 'degraded':
        return '🔄';
      case 'checking':
        return '🔍';
      default:
        return '❌';
    }
  }}
`;

const StatusTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
`;

const StatusMessage = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 1.6;
  max-width: 500px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  border: none;

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
        background-color: ${theme.colors.primary};
        color: ${theme.colors.white};

        &:hover:not(:disabled) {
          background-color: ${theme.colors.secondary};
          transform: translateY(-1px);
        }
      `
      : `
        background-color: transparent;
        color: ${theme.colors.gray.dark};
        border: 1px solid ${theme.colors.gray.medium};

        &:hover:not(:disabled) {
          background-color: ${theme.colors.gray.light};
        }
      `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusDetails = styled.details`
  margin-top: ${({ theme }) => theme.spacing.md};
  max-width: 500px;
`;

const StatusSummary = styled.summary`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  user-select: none;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const StatusDetailContent = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
  text-align: left;
`;

const LastChecked = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray.medium};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export interface ServiceOutageIndicatorProps {
  error?: ApiError;
  variant?: 'banner' | 'card' | 'inline';
  onRetry?: () => void;
  onCheckStatus?: () => void;
  showDetails?: boolean;
  customTitle?: string;
  customMessage?: string;
  className?: string;
}

export function ServiceOutageIndicator({
  error,
  variant = 'card',
  onRetry,
  onCheckStatus,
  showDetails = true,
  customTitle,
  customMessage,
  className,
}: ServiceOutageIndicatorProps) {
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const handleRetry = async () => {
    if (!onRetry) return;
    
    setIsChecking(true);
    try {
      await onRetry();
      setLastChecked(new Date());
    } finally {
      setIsChecking(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!onCheckStatus) return;
    
    setIsChecking(true);
    try {
      await onCheckStatus();
      setLastChecked(new Date());
    } finally {
      setIsChecking(false);
    }
  };

  // Auto-update last checked time
  useEffect(() => {
    if (lastChecked) {
      const interval = setInterval(() => {
        setLastChecked(new Date(lastChecked));
      }, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [lastChecked]);

  const getStatusInfo = () => {
    if (isChecking) {
      return {
        status: 'checking' as const,
        title: 'Checking Service Status...',
        message: 'Please wait while we verify if the service is available.',
      };
    }

    if (error?.type === 'service_outage') {
      return {
        status: 'outage' as const,
        title: customTitle || 'Service Temporarily Unavailable',
        message: customMessage || error.message,
      };
    }

    if (error?.type === 'network') {
      return {
        status: 'outage' as const,
        title: customTitle || 'Connection Issue',
        message: customMessage || 'Unable to connect to our servers. Please check your internet connection.',
      };
    }

    if (error?.type === 'server_error') {
      return {
        status: 'degraded' as const,
        title: customTitle || 'Service Issues',
        message: customMessage || 'Our service is experiencing some issues. We are working to resolve them.',
      };
    }

    return {
      status: 'outage' as const,
      title: customTitle || 'Service Issue',
      message: customMessage || error?.message || 'An unexpected error occurred.',
    };
  };

  const { status, title, message } = getStatusInfo();

  const formatLastChecked = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    return date.toLocaleString();
  };

  return (
    <OutageContainer $variant={variant} className={className}>
      <StatusIcon $status={status}>
        {status === 'outage' && '⚠️'}
        {status === 'degraded' && '🔄'}
        {status === 'checking' && '🔍'}
      </StatusIcon>
      
      <div>
        <StatusTitle>{title}</StatusTitle>
        <StatusMessage>{message}</StatusMessage>
      </div>

      <ActionButtons>
        {onRetry && error?.isRetryable && (
          <ActionButton
            $variant="primary"
            onClick={handleRetry}
            disabled={isChecking}
          >
            {isChecking ? 'Retrying...' : 'Try Again'}
          </ActionButton>
        )}
        
        {onCheckStatus && (
          <ActionButton
            $variant="secondary"
            onClick={handleCheckStatus}
            disabled={isChecking}
          >
            Check Status
          </ActionButton>
        )}
        
        {!onRetry && !onCheckStatus && (
          <ActionButton
            $variant="secondary"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </ActionButton>
        )}
      </ActionButtons>

      {showDetails && error && (
        <StatusDetails>
          <StatusSummary>Technical Details</StatusSummary>
          <StatusDetailContent>
            <div><strong>Error Type:</strong> {error.type}</div>
            <div><strong>Status Code:</strong> {error.status || 'N/A'}</div>
            {error.errors && (
              <div>
                <strong>Details:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {Object.entries(error.errors).map(([field, fieldErrors]) =>
                    fieldErrors.map((fieldError, index) => (
                      <li key={`${field}-${index}`}>
                        {field}: {fieldError}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </StatusDetailContent>
        </StatusDetails>
      )}

      {lastChecked && (
        <LastChecked>
          Last checked: {formatLastChecked(lastChecked)}
        </LastChecked>
      )}
    </OutageContainer>
  );
}

// Preset configurations for common use cases
export function ServiceMaintenanceIndicator(props: Omit<ServiceOutageIndicatorProps, 'customTitle' | 'customMessage'>) {
  return (
    <ServiceOutageIndicator
      {...props}
      customTitle="Scheduled Maintenance"
      customMessage="We are currently performing scheduled maintenance. The service will be back online shortly."
    />
  );
}

export function NetworkErrorIndicator(props: Omit<ServiceOutageIndicatorProps, 'customTitle' | 'customMessage'>) {
  return (
    <ServiceOutageIndicator
      {...props}
      customTitle="Connection Problem"
      customMessage="Unable to connect to our servers. Please check your internet connection and try again."
    />
  );
}

export function BackendOutageIndicator(props: Omit<ServiceOutageIndicatorProps, 'customTitle' | 'customMessage'>) {
  return (
    <ServiceOutageIndicator
      {...props}
      customTitle="Service Temporarily Down"
      customMessage="Our servers are currently experiencing issues. We are working to restore service as quickly as possible."
    />
  );
}